import { useState } from "react";
import { TrendingUp, Sigma, Wallet, Clock, Target, ChevronDown, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AssumptionsPanel, money, compact } from "@/components/equity/AssumptionsPanel";
import { formatDate } from "@/lib/format";

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="rounded-xl border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="mt-1 h-display text-2xl font-semibold tabular-nums text-foreground">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function VestingBar({ vesting, options }) {
  const pct = Math.round((vesting.fraction || 0) * 100);
  return (
    <Card className="rounded-xl border bg-card p-4" data-testid="vesting-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-foreground">Vesting</div>
        <div className="text-xs text-muted-foreground">
          {vesting.assumed_fully_vested ? "No vesting schedule on file - treated as fully vested" :
            `${vesting.vesting_months}-month schedule · ${vesting.cliff_months}-month cliff · started ${formatDate(vesting.vesting_start)}`}
        </div>
      </div>
      <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-primary transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs">
        <span className="font-semibold text-foreground" data-testid="vested-summary">{Math.round(vesting.vested_options).toLocaleString()} of {Math.round(options).toLocaleString()} vested ({pct}%)</span>
        <span className="text-muted-foreground">
          {vesting.next_vest_on && `Next vest ${formatDate(vesting.next_vest_on)} · `}{vesting.fully_vested_on && `Fully vested ${formatDate(vesting.fully_vested_on)}`}
        </span>
      </div>
    </Card>
  );
}

function ScenarioTable({ scenarios, strike }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="scenario-table">
        <thead>
          <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="py-2 pr-3">Exit value</th><th className="py-2 pr-3 text-right">Common $/share</th><th className="py-2 pr-3 text-right">Gross</th>
            <th className="py-2 pr-3 text-right">Exercise cost</th><th className="py-2 pr-3 text-right">Pre-tax gain</th>
            <th className="py-2 pr-3 text-right">Net · exercise at exit</th><th className="py-2 pr-3 text-right">Net · exercise now &amp; hold</th><th className="py-2 text-right">Cash needed now</th>
          </tr>
        </thead>
        <tbody>
          {scenarios.map((s, i) => (
            <tr key={i} className={`border-b border-border/60 ${s.in_the_money ? "" : "text-muted-foreground"}`} data-testid={`scenario-row-${i}`}>
              <td className="py-2.5 pr-3 font-semibold text-foreground">{compact(s.exit_value)}{s.multiple_of_post_money != null && <span className="ml-1 text-[11px] font-normal text-muted-foreground">{s.multiple_of_post_money.toFixed(1)}×</span>}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{money(s.common_price, 2)}{!s.in_the_money && <Badge variant="secondary" className="ml-1 font-normal">≤ strike {money(strike, 2)}</Badge>}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{money(s.gross_value)}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums">{money(s.exercise_cost)}</td>
              <td className="py-2.5 pr-3 text-right font-semibold tabular-nums text-foreground" data-testid={`scenario-gain-${i}`}>{money(s.pre_tax_gain)}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-emerald-700">{money(s.exercise_at_exit.net)}</td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-emerald-700">{money(s.exercise_now_hold.net)}</td>
              <td className="py-2.5 text-right tabular-nums">{money(s.exercise_now_hold.cash_needed_upfront)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {scenarios[0] && (
        <div className="mt-3 grid gap-2 text-[11px] text-muted-foreground sm:grid-cols-2">
          <div className="rounded-lg bg-secondary/60 p-2.5"><span className="font-semibold text-foreground">Exercise at exit:</span> {scenarios[0].exercise_at_exit.notes.join(". ")}.</div>
          <div className="rounded-lg bg-secondary/60 p-2.5"><span className="font-semibold text-foreground">Exercise now &amp; hold:</span> {scenarios[0].exercise_now_hold.notes.join(". ")}.</div>
        </div>
      )}
    </div>
  );
}

export function OptionValuationView({ valuation, assumptions, onAssumptionsChange, loading, children }) {
  const [openAssumptions, setOpenAssumptions] = useState(false);
  if (!valuation) return null;
  const { today: t, grant: g, company: c, scenarios, break_even: be } = valuation;
  const bs = t.black_scholes_total_fmv ?? t.black_scholes_total_preferred;
  return (
    <div className={`space-y-6 transition-opacity ${loading ? "opacity-60" : ""}`} data-testid="option-valuation">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={Clock} label="Vested value today" value={money(t.intrinsic_vested)} sub={`${money(t.intrinsic_per_option, 2)} per option · ${t.spot_basis || "no price yet"}`} testid="stat-intrinsic-vested" />
        <Stat icon={TrendingUp} label="Intrinsic value (all)" value={money(t.intrinsic_total)} sub={`${Math.round(g.options).toLocaleString()} ${g.option_type}s at ${money(g.strike, 2)} strike`} testid="stat-intrinsic-total" />
        <Stat icon={Sigma} label="Fair value (Black-Scholes)" value={money(bs)} sub={t.black_scholes_per_option_fmv != null ? `${money(t.black_scholes_per_option_fmv, 2)}/option on 409A · ${money(t.black_scholes_per_option_preferred, 2)} on preferred` : "Add a share price to compute"} testid="stat-black-scholes" />
        <Stat icon={Wallet} label="Cost to exercise" value={money(t.exercise_cost_total)} sub={`${money(t.exercise_cost_vested)} for vested · ${t.ownership_pct_fd != null ? `${t.ownership_pct_fd.toFixed(3)}% fully diluted` : "ownership n/a"}`} testid="stat-exercise-cost" />
      </div>

      <VestingBar vesting={g.vesting} options={g.options} />

      {be && (
        <Card className="flex flex-wrap items-center gap-4 rounded-xl border border-brand/30 bg-brand/[0.06] p-4" data-testid="break-even-card">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand/15 text-brand-ink"><Target className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground">Break-even exit: <span data-testid="break-even-value">{compact(be.exit_value)}</span>{be.multiple_of_post_money != null && <span className="ml-1 text-xs font-normal text-muted-foreground">({be.multiple_of_post_money.toFixed(2)}× today's valuation)</span>}</div>
            <div className="text-xs text-muted-foreground">{be.explanation} Liquidation preferences ahead of common: {money(c.total_preference)}.</div>
          </div>
        </Card>
      )}

      <Card className="rounded-xl border bg-card p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Exit scenarios</h3>
            <p className="text-xs text-muted-foreground">Exit value → preferences paid first → common price per share → your options ({Math.round(scenarios[0]?.options_at_exit || 0).toLocaleString()} vested by exit) · {Math.round(assumptions.expected_dilution_pct ?? 20)}% further dilution</p>
          </div>
          <button type="button" onClick={() => setOpenAssumptions((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary" data-testid="toggle-assumptions">
            Adjust assumptions <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openAssumptions ? "rotate-180" : ""}`} />
          </button>
        </div>
        {openAssumptions && <div className="mb-5 rounded-xl border bg-secondary/40 p-4"><AssumptionsPanel assumptions={assumptions} onChange={onAssumptionsChange} postMoney={c.post_money} /></div>}
        <ScenarioTable scenarios={scenarios} strike={g.strike} />
      </Card>

      {children}

      <p className="flex items-start gap-2 text-[11px] leading-relaxed text-muted-foreground" data-testid="valuation-disclaimer"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {valuation.disclaimer}</p>
    </div>
  );
}
